// CPI-848: which contact the VN booking screen reads for each purchaser.
//
// When a booking reaches paid_verified, the backend clones every purchaser's live contact
// into a "snapshot" row and stores {customer_id, snapshot_contact_id, ...} refs on the
// opportunity (snapshot_contacts) plus a snapshot_frozen_at stamp. From then on the screen
// reads the snapshot copy, so later edits to the customer's own record no longer rewrite the
// details of an already-verified sale.
//
// Before the freeze — and for every booking created before this shipped — there are no
// refs, and the screen reads customer.contact_ids[0] exactly as it always did. That fallback
// is the whole backward-compatibility story; nothing else special-cases old bookings.
//
// Resolution is PER PURCHASER, never per booking. A freeze can legitimately produce a ref
// for one purchaser and none for another (a customer with no contact_ids is skipped), and
// that purchaser must still render from live data rather than blank.

export const getSnapshotContacts = (source) => (
  Array.isArray(source?.snapshot_contacts) ? source.snapshot_contacts : []
);

export const isSnapshotFrozen = (source) => Boolean(source?.snapshot_frozen_at);

// The ref for one purchaser, or null. A ref with no snapshot_contact_id counts as absent so
// the caller falls back to the live contact instead of querying an empty id.
export const snapshotContactFor = (source, customerId) => {
  if (!customerId) return null;
  return getSnapshotContacts(source)
    .find((ref) => ref && ref.customer_id === customerId && ref.snapshot_contact_id) || null;
};

// The contact id the screen should read for this purchaser: the snapshot when one exists,
// otherwise the live contact, otherwise ''.
export const purchaserContactId = (source, customer) => (
  snapshotContactFor(source, customer?.id)?.snapshot_contact_id
  || customer?.contact_ids?.[0]
  || ''
);

// Stable dependency keys for effects. The queries here run with fetchPolicy 'no-cache', which
// hands back a fresh array object on every fetch, so array identity is useless as a dep.
// Both keys are order-independent and ignore refs that could never resolve, so identical
// data always yields an identical key.
export const snapshotRefsKey = (source) => getSnapshotContacts(source)
  .filter((ref) => ref && ref.customer_id && ref.snapshot_contact_id)
  .map((ref) => `${ref.customer_id}:${ref.snapshot_contact_id}`)
  .sort()
  .join('|');

// One entry per purchaser with the contact id the screen would READ for them. Changes when
// a snapshot ref arrives AND when a live contact_ids[0] churns (pre-freeze versioning), so an
// effect keyed on it refetches in exactly those two cases and no others.
export const purchaserContactKey = (source, customers) => (
  Array.isArray(customers) ? customers : []
)
  .map((customer) => `${customer?.id || ''}:${purchaserContactId(source, customer)}`)
  .sort()
  .join('|');

// CPI-848: the only fields a purchaser snapshot may be corrected in. Mirrors the server-side
// allowlist in mhub-contact-api (which is what actually enforces it); this copy exists so the
// drawer sends only what can be saved. Excluded on purpose: name, mobile, email (buyer-portal
// login keys and de-dupe keys) and every identity number / document (already printed into
// the receipt and booking form).
export const SNAPSHOT_EDITABLE_FIELDS = [
  'title', 'gender', 'date_of_birth', 'marital_status', 'occupation', 'race', 'bumi',
  'citizenship', 'nationality_country_code', 'monthly_income', 'relationship_type',
  'address_1', 'address_2', 'address_3', 'address_4', 'address_5',
  'ward', 'city', 'state', 'postcode', 'country_code',
  'bank_name', 'bank_account_number', 'bank_account_name',
];

// The update payload for a purchaser snapshot: only allowlisted keys, only those that changed.
// Returns {} when there is nothing to save, so the caller can skip the mutation.
// A key missing from `values` is a CLEAR when the snapshot had a value for it - RegisterCustomer
// deletes a key when its input is emptied - and untouched when the snapshot had none either.
// RegisterCustomer edits address line 1 under the legacy key `address` when the company's
// field settings have no `address_1`; the snapshot row (and the server allowlist) only know
// `address_1`, so that key is folded in here rather than dropped.
const withLegacyAddress = (form) => {
  if (!('address' in form) || 'address_1' in form) return form;
  const { address, ...rest } = form;
  return { ...rest, address_1: address };
};

export const buildSnapshotUpdate = (original, values) => {
  const out = {};
  const form = withLegacyAddress(values || {});
  SNAPSHOT_EDITABLE_FIELDS.forEach((key) => {
    const prev = original?.[key] == null ? '' : String(original[key]);
    if (!(key in form) && prev === '') return;
    const next = form[key] == null ? '' : String(form[key]);
    if (next !== prev) out[key] = next;
  });
  return out;
};
